<template>
    <rkm-area
        type="fieldset"
        title="Pessoas avaliadas"
        show-legend
    >
        <rkm-row v-if="data.form">
            <rkm-col cols="4">
                <rkm-form-input-autocomplete
                    :disabled="isAtividadeDisabled"
                    :repository="repositories.paciente"
                    :search-fields="fields.autocomplete.paciente"
                    :text-field="(item) =>  item.cidadao.cns ? `${this.formatCns(item.cidadao.cns)} - ${item.cidadao.nome}` : item.cidadao.nome"
                    :validations="validations.paciente"
                    label="Paciente"
                    placeholder="Nome, Nome social, CNS, CPF, Data de nascimento ou Prontuário"
                    uppercase
                    v-model="data.model.participante.cidadao"
                />
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-input
                    v-model="data.model.participante.peso"
                    append="kg"
                    label="Peso"
                    placeholder="000.00"
                    :formatter="formatMedicao"
                    :validations="validations.peso"
                    :disabled="isAtividadeDisabled || isProcedimentoDisabled(enums.procedimento.MEDICAO_DE_PESO)"
                />
            </rkm-col>

            <rkm-col cols="2">
                <rkm-form-input
                    v-model="data.model.participante.altura"
                    append="cm"
                    label="Altura"
                    placeholder="000.00"
                    :formatter="formatMedicao"
                    :validations="validations.altura"
                    :disabled="isAtividadeDisabled || isProcedimentoDisabled(enums.procedimento.MEDICAO_DE_ALTURA)"
                />
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-input
                    disabled
                    label="IMC"
                    v-model="imc.imc"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            {{ imc.classificacao || '-' }}
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input>
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-input
                    v-model="data.model.participante.afericaoPa"
                    label="Pressão arterial"
                    placeholder="000/000"
                    mask="###/###"
                    :validations="validations.afericaoPa"
                    :disabled="isAtividadeDisabled || isProcedimentoDisabled(enums.procedimento.AFERICAO_PRESSAO_ARTERIAL)"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            mmHg
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input>
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-input
                    v-model="data.model.participante.glicemiaCapilar"
                    label="Glicemia capilar"
                    placeholder="000"
                    mask="###"
                    :validations="validations.glicemiaCapilar"
                    :disabled="isAtividadeDisabled || isProcedimentoDisabled(enums.procedimento.GLICEMIA_CAPILAR)"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            mg/dL
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input>
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-select
                    :options="data.form.momentosColeta"
                    :validations="validations.momentoColeta"
                    label="Momento da coleta"
                    show-default-option
                    text-field="descricao"
                    default-option-text="Selecione"
                    value-field="id"
                    v-model="data.model.participante.momentoColetaId"
                    :disabled="isAtividadeDisabled || isProcedimentoDisabled(enums.procedimento.GLICEMIA_CAPILAR)"
                />
            </rkm-col>

            <rkm-col cols="2">
                <rkm-form-checkbox
                    class="mt-4"
                    :value="true"
                    v-model="data.model.participante.avaliacaoAlterada"
                    :disabled="isAtividadeDisabled"
                >
                    Avaliação alterada
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="2">
                <rkm-form-checkbox
                    class="mt-4"
                    :value="true"
                    v-model="data.model.participante.parouFumar"
                    :disabled="isAtividadeDisabled || isPnctDisabled"
                >
                    PNCT - Parou de fumar
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="3">
                <rkm-form-checkbox
                    class="mt-4"
                    :value="true"
                    v-model="data.model.participante.abandonouGrupo"
                    :disabled="isAtividadeDisabled || isPnctDisabled"
                >
                    PNCT - Abandonou Grupo
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="5" class="text-right">
                <rkm-button
                    @click="save"
                    color="primary"
                    class="mt-4"
                >
                    Incluir
                </rkm-button>
            </rkm-col>
        </rkm-row>
    </rkm-area>
</template>

<script>

// Vuex.
import { mapActions } from 'vuex'

// Mixins
import { repository, error, object, formats, data, stringCase } from '@/mixins'

//Enums
import saudePratica from '@/enums/atencao-basica/atividade-coletiva/saudePratica'
import atividadeTipo from '@/enums/atencao-basica/atividade-coletiva/atividadeTipo'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import unidadeMedida from '@/enums/cadastro/unidadeMedida'
import procedimento from '@/enums/sigtap/procedimento'

export default {
    data: () => ({
        isPnctDisabled: true,
        digitosMedicao: 5,
        procedimentosUsuario: [],
        imc: {
            imc: null,
            classificacao: null,
        },
        model: {
            usuario: null,
            participantes: null
        },
        enums: {
            saudePratica,
            atividadeTipo,
            subclassificacao,
            unidadeMedida,
            procedimento
        }
    }),
    computed: {
        isAtividadeDisabled() {
            if(this.data.model.atividade.totalParticipantes == '' || this.data.model.atividade.totalParticipantes == this.data.model.participantes.length) {

                return true
            } else if(this.data.model.atividade.totalParticipantes >= this.data.model.participantes.length) {

                return false
            } else if(this.data.model.atividade.totalParticipantes != '' && this.data.model.atividade.totalParticipantes < this.data.model.participantes.length) {

                return true
            } else {

                return true
            }
        },
        validations() {
            return {
                paciente: {
                    required: this.isRequiredPaciente
                },
                peso: {
                    min_value: 0.5,
                    max_value: 500
                },
                altura: {
                    min_value: 20,
                    max_value: 250
                },
                afericaoPa: {
                    min: 7,
                    max: 7
                },
                glicemiaCapilar: {
                    min_value: 0,
                    max_value: 800,
                    required: this.data.model.participante.momentoColetaId ? true : false
                },
                momentoColeta: {
                    required: this.data.model.participante.glicemiaCapilar ? true : false
                }
            }
        },
        isRequiredPaciente() {
            const tiposAtividade = [
                this.enums.atividadeTipo.ATENDIMENTO_EM_GRUPO,
                this.enums.atividadeTipo.AVALIACAO_PROCEDIMENTO_COLETIVO
            ]

            return tiposAtividade.includes(this.data.model.atividade.atividadeTipoId)
        },
        fields() {
            return {
                autocomplete: {
                    usuario: [
                        { key: 'cadastroCidadao.nome', searchable: true },
                        { key: 'cadastroUsuario.registroNumero', searchable: true },
                    ],
                    paciente: [
                        { key: 'cidadao.nome', searchable: true },
                        { key: 'cidadao.nomeSocial', searchable: true },
                        { key: 'cidadao.cns', searchable: true },
                        { key: 'cidadao.cpf', searchable: true },
                        { key: 'cidadao.data_nascimento', searchable: true },
                        { key: 'prontuario', searchable: true },
                    ],
                },
            }
        },

        imcRequest() {

            const peso = parseFloat(String(this.data.model.participante.peso ?? '').replace(',', '.'))
            const altura = parseFloat(String(this.data.model.participante.altura ?? '').replace(',', '.'))

            const isPesoValido = peso >= this.validations.peso.min_value && peso <= this.validations.peso.max_value
            const isAlturaValida = altura >= this.validations.altura.min_value && altura <= this.validations.altura.max_value

            if (!isPesoValido || !isAlturaValida) {

                return null
            }

            return {
                unidade_medida_massa_id: this.enums.unidadeMedida.QUILOGRAMAS,
                massa: peso,
                unidade_medida_altura_id: this.enums.unidadeMedida.CENTIMETROS,
                altura: altura,
            }
        },
    },
    created() {
        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            procedimento: this.getConectaSusRepository('atencaoBasica.procedimento'),
            geral: this.getConectaSusRepository('geral')
        }

        this.getProcedimentosUsuario()
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        getProcedimentosUsuario() {

            const ocupacaoId = this.data.model.profissional.ocupacaoId

            if (!ocupacaoId) {

                this.procedimentosUsuario = []

                return
            }

            this.repositories.procedimento.getProcedimentosUsuario({ ocupacao_id: ocupacaoId })
                .then(({ data }) => this.procedimentosUsuario = data)
                .catch(() => this.procedimentosUsuario = [])
        },
        isProcedimentoDisabled(codigo) {

            const procedimento = this.procedimentosUsuario.find(procedimento => procedimento.codigo == codigo)

            return procedimento ? procedimento.isDisabled : false
        },
        formatMedicao(value) {

                const digitos = String(value ?? '')
                .replace(/\D/g, '')
                .replace(/^0+/, '')
                .slice(0, this.digitosMedicao)

            return digitos ? (Number(digitos) / 100).toFixed(2) : ''
        },
        calcularImc() {

            const request = this.imcRequest

            if (!request) {

                this.imc = { imc: null, classificacao: null }

                return
            }

            this.repositories.geral.calcularIMC(request)
                .then(({ data }) => this.imc = data)
                .catch(() => this.imc = { imc: null, classificacao: null })
        },
        save() {

            if(this.data.model.participante.cidadao) {
                const participante = this.data.model.participantes.find(participante => participante.id == this.data.model.participante.cidadao.id)

                if (participante) {

                    this.showAlert('O participante já foi cadastrado anteriormente na ficha.', "Atenção")
                    return false
                }

                const param = {
                    id: this.data.model.participante.cidadao.id,
                    nome: this.data.model.participante.cidadao.cidadao.nome,
                    nomeSocial: this.data.model.participante.cidadao.cidadao.nomeSocial,
                    cns: this.data.model.participante.cidadao.cidadao.cns,
                    avaliacaoAlterada: this.data.model.participante.avaliacaoAlterada ? 'Sim' : '-',
                    peso: this.data.model.participante.peso || null,
                    altura: this.data.model.participante.altura || null,
                    imc: this.imc.imc ?? null,
                    imcClassificacao: this.imc.classificacao ?? null,
                    afericaoPa: this.data.model.participante.afericaoPa ?? null,
                    glicemiaCapilar: this.data.model.participante.glicemiaCapilar ?? null,
                    momentoColetaId: this.data.model.participante.momentoColetaId ?? null,
                    parouFumar: this.data.model.participante.parouFumar ? 'Sim' : '-',
                    abandonouGrupo: this.data.model.participante.abandonouGrupo ? 'Sim' : '-'
                }

                if (this.index !== null) {

                    this.$set(this.data.model.participantes, this.index, this.clone(param))
                } else {

                    this.data.model.participantes.push(this.clone(param))
                }

                this.data.model.atividade.avaliacoesAlteradas = this.data.model.participantes.filter(participante => participante.avaliacaoAlterada == 'Sim').length

                this.resetForm()
            }
        },
        resetForm() {

            this.data.model.participante = {
                cidadao: null,
                peso: null,
                altura: null,
                afericaoPa: null,
                glicemiaCapilar: null,
                momentoColetaId: null,
                avaliacaoAlterada: null,
                parouFumar: null,
                abandonouGrupo: null,
            }

            this.imc = { imc: null, classificacao: null }
        },
    },
    mixins: [repository, error, object, formats, data, stringCase],
    props: {
        data: { type: Object, required: false },
        id: { type: Number },
        index: { type: Number, default: null },
    },
    watch: {
        "data.model.profissional.ocupacaoId": function () {

            this.getProcedimentosUsuario()
        },
        "data.model.participante.peso": function () {

            this.calcularImc()
        },
        "data.model.participante.altura": function () {

            this.calcularImc()
        },
        "data.model.atividade.totalParticipantes": function () {

            if(this.data.model.atividade.totalParticipantes != '' && this.data.model.atividade.totalParticipantes < this.data.model.participantes.length) {

                this.showAlert('A quantidade de participantes não pode ser menor do que os participantes cadastrados.', "Atenção")

                this.data.model.atividade.totalParticipantes = this.data.model.participantes.length

            }
        },
        "data.model.saudePratica": function (pratica) {

            const tiposPratica = [
                this.enums.saudePratica.PROGRAMA_NACIONAL_DE_CONTROLE_DO_TABAGISMO_1,
                this.enums.saudePratica.PROGRAMA_NACIONAL_DE_CONTROLE_DO_TABAGISMO_2,
                this.enums.saudePratica.PROGRAMA_NACIONAL_DE_CONTROLE_DO_TABAGISMO_3,
                this.enums.saudePratica.PROGRAMA_NACIONAL_DE_CONTROLE_DO_TABAGISMO_4
            ]

            if (tiposPratica.some(r => pratica.indexOf(r) >= 0)) {
                return this.isPnctDisabled = false
            } else {
                return this.isPnctDisabled = true
            }
        },
        "data.model.participante.cidadao.id": function () {

            const participante = this.data.model.participante.cidadao

            if(participante) {

                this.setLoading()

                this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: participante.id}))
                    .then(response => {

                        if (!response.data.additionals.cnsValido && response.data.additionals.avisaCnsTemporarioInvalido) {

                            this.showWarningNotification('Paciente com CNS inválido. Registro não permitido.')
                            this.data.model.participante.cidadao = null
                            return false
                        }

                        if (participante.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                            && response.data.additionals.avisaCnsTemporarioInvalido) {

                                const confirm = async () => {

                                    return false
                                }

                                const cancelar = async () => {

                                    this.data.model.participante.cidadao = null
                                    return false
                                }

                                const msg = `Paciente com cadastro temporário. Deseja continuar?`

                                this.showConfirmation(msg, confirm, cancelar)

                                return false
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        }
    },
}
</script>